import React from 'react';
import { InitialMessageVideo } from '../components/InitialMessageVideo';

/**
 * Simple markdown renderer for chat messages
 * Handles basic markdown syntax and HTML video elements
 */
export const renderMarkdown = (content: string, isInitialMessage: boolean = false): React.ReactNode => {
  // Split content by lines for processing
  const lines = content.split('\n');
  const elements: React.ReactNode[] = [];
  let currentIndex = 0;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    
    // Handle headers
    if (line.startsWith('# ')) {
      elements.push(
        <h1 key={`h1-${currentIndex++}`} className="markdown-h1">
          {line.substring(2)}
        </h1>
      );
      continue;
    }
    
    if (line.startsWith('## ')) {
      elements.push(
        <h2 key={`h2-${currentIndex++}`} className="markdown-h2">
          {line.substring(3)}
        </h2>
      );
      continue;
    }
    
    if (line.startsWith('### ')) {
      elements.push(
        <h3 key={`h3-${currentIndex++}`} className="markdown-h3">
          {line.substring(4)}
        </h3>
      );
      continue;
    }

    // Handle HTML video elements
    if (line.trim().startsWith('<video')) {
      // Find the closing video tag
      let videoHtml = line;
      let j = i;
      
      while (j < lines.length && !videoHtml.includes('</video>')) {
        j++;
        if (j < lines.length) {
          videoHtml += '\n' + lines[j];
        }
      }
      
      // Extract video attributes
      const srcMatch = videoHtml.match(/src="([^"]+)"/);
      const widthMatch = videoHtml.match(/width="([^"]+)"/);
      const styleMatch = videoHtml.match(/style="([^"]+)"/);
      const controlsMatch = videoHtml.includes('controls');
      
      if (srcMatch) {
        // Use specialized component for initial message videos with autoplay
        if (isInitialMessage) {
          elements.push(
            <InitialMessageVideo
              key={`video-${currentIndex++}`}
              src={srcMatch[1]}
              autoplay={true}
              style={styleMatch ? parseInlineStyle(styleMatch[1]) : { maxWidth: '500px', borderRadius: '8px' }}
              className="markdown-video"
            />
          );
        } else {
          // Regular video for other messages
          elements.push(
            <div key={`video-${currentIndex++}`} className="markdown-video-container">
              <video
                src={srcMatch[1]}
                controls={controlsMatch}
                width={widthMatch ? widthMatch[1] : '100%'}
                style={styleMatch ? parseInlineStyle(styleMatch[1]) : { maxWidth: '500px', borderRadius: '8px' }}
                className="markdown-video"
                loop={false}
              >
                Your browser does not support the video tag.
              </video>
            </div>
          );
        }
      }
      
      // Skip the lines we've processed
      i = j;
      continue;
    }

    // Handle empty lines
    if (line.trim() === '') {
      elements.push(<br key={`br-${currentIndex++}`} />);
      continue;
    }

    // Handle regular text with inline formatting
    const processedLine = processInlineMarkdown(line);
    elements.push(
      <p key={`p-${currentIndex++}`} className="markdown-p">
        {processedLine}
      </p>
    );
  }

  return <div className="markdown-content">{elements}</div>;
};

/**
 * Process inline markdown formatting (bold, italic, etc.)
 */
const processInlineMarkdown = (text: string): React.ReactNode => {
  // Handle bold text (**text**)
  let processed = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  
  // Handle italic text (*text*)
  processed = processed.replace(/\*(.*?)\*/g, '<em>$1</em>');
  
  // Handle inline code (`code`)
  processed = processed.replace(/`(.*?)`/g, '<code>$1</code>');
  
  // Handle links [text](url)
  processed = processed.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');

  // If no HTML tags were added, return as plain text
  if (processed === text) {
    return text;
  }

  // Return as HTML (be careful with XSS in production)
  return <span dangerouslySetInnerHTML={{ __html: processed }} />;
};

/**
 * Parse inline CSS style string to React style object
 */
const parseInlineStyle = (styleString: string): React.CSSProperties => {
  const style: React.CSSProperties = {};
  
  styleString.split(';').forEach(declaration => {
    const [property, value] = declaration.split(':').map(s => s.trim());
    if (property && value) {
      // Convert kebab-case to camelCase
      const camelProperty = property.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());
      (style as any)[camelProperty] = value;
    }
  });
  
  return style;
};